import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import SingleSelect from "../../../../components/commonComponents/Selectors/SingleSelect";
import { checkUserRole } from "../../../../api_calls/userManagement";
import {
  findAnalyticsCampaignStoreIds,
  getAnalyticsCampaignStoreCatalog,
  getAnalyticsCampaignStores,
  intersectWithAnalyticsStoreCatalog,
} from "../../../../api_calls/dashboardFilterSeed";

/**
 * Single-select Stores filter for campaign analytics.
 * Options and ids come from analytics_campaign_stores_list.
 */
const CampaignStoreSingleSelectFilter = ({
  reduxKeywordToSaveValue,
  filterParamRegionName,
  filterParamNielsenRegionCode,
  regionFilterEnabled,
  nielsenRegionFilterEnabled,
}) => {
  const storeDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const [storeList, setStoreList] = React.useState([]);
  const [selectedStore, setSelectedStore] = React.useState(null);
  const [dataLoaded, setDataLoaded] = React.useState(false);

  const userRole =
    storeDetails?.brand_report_view === true
      ? { user_role: "Brand" }
      : checkUserRole();

  const brandStoreBagKey =
    reduxKeywordToSaveValue === "embedded_impression_filter"
      ? "impression_filter"
      : "performance_filter";

  const filterStoreOptions = () => {
    const regionsSelected =
      storeDetails?.[reduxKeywordToSaveValue]?.[filterParamRegionName] || [];
    const nielsenRegionsSelected =
      storeDetails?.[reduxKeywordToSaveValue]?.[filterParamNielsenRegionCode] ||
      [];

    let campaignStores = getAnalyticsCampaignStoreCatalog();

    if (userRole.user_role === "Brand") {
      const brandStores =
        storeDetails.brand_filters?.[brandStoreBagKey]?.stores || [];
      campaignStores = intersectWithAnalyticsStoreCatalog(brandStores).sort();
    }

    if (!regionFilterEnabled && !nielsenRegionFilterEnabled) {
      return campaignStores;
    }

    const analyticsStores = getAnalyticsCampaignStores();

    // Filter using region data on the analytics campaign store objects.
    if (regionFilterEnabled && regionsSelected.length > 0) {
      return analyticsStores
        .filter(
          (store) =>
            campaignStores.includes(store.name) &&
            regionsSelected.includes(store.region?.name)
        )
        .map((store) => store.name)
        .sort();
    }

    if (
      nielsenRegionFilterEnabled &&
      !regionFilterEnabled &&
      nielsenRegionsSelected.length > 0
    ) {
      return analyticsStores
        .filter(
          (store) =>
            campaignStores.includes(store.name) &&
            nielsenRegionsSelected.includes(store.region?.nielsenRegion)
        )
        .map((store) => store.name)
        .sort();
    }

    return campaignStores;
  };

  const writeSingleStoreSelection = (storeName, bagSnapshot) => {
    const selectedOpt = { value: storeName, label: storeName };
    setSelectedStore(selectedOpt);
    dispatchAction(
      updateStateData({
        [reduxKeywordToSaveValue]: {
          ...bagSnapshot,
          store_ids: findAnalyticsCampaignStoreIds([storeName]),
          selected_store_names: [selectedOpt],
        },
      })
    );
  };

  /** Prefer page-load seed when it is still a valid option; otherwise first in list. */
  const resolveInitialStore = (list) => {
    const seededNames =
      storeDetails[reduxKeywordToSaveValue]?.selected_store_names || [];
    const seededLabel = seededNames[0]?.label;
    if (seededLabel && list.includes(seededLabel)) {
      return seededLabel;
    }
    return list[0];
  };

  const storeOptions = React.useMemo(
    () => storeList.map((n) => ({ value: n, label: n })),
    [storeList]
  );

  // Skip the first region-effect run (mount) so it does not overwrite the seed.
  const skipInitialRegionEffect = React.useRef(true);

  React.useEffect(() => {
    const list = filterStoreOptions();
    setStoreList(list);
    if (list.length > 0) {
      writeSingleStoreSelection(
        resolveInitialStore(list),
        storeDetails[reduxKeywordToSaveValue]
      );
    } else {
      setSelectedStore(null);
    }
    setDataLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (skipInitialRegionEffect.current) {
      skipInitialRegionEffect.current = false;
      return;
    }

    const list = filterStoreOptions();

    if (list.length > 0) {
      setStoreList(list);
      writeSingleStoreSelection(list[0], storeDetails[reduxKeywordToSaveValue]);
    } else {
      setStoreList([]);
      setSelectedStore(null);
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: [],
            selected_store_names: [],
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeDetails?.[reduxKeywordToSaveValue]?.[filterParamRegionName],
    storeDetails?.[reduxKeywordToSaveValue]?.[filterParamNielsenRegionCode],
  ]);

  const handleOnChange = (option) => {
    if (option) {
      writeSingleStoreSelection(
        option.label,
        storeDetails[reduxKeywordToSaveValue]
      );
    }
  };

  return dataLoaded ? (
    <SingleSelect
      key={storeOptions.map((o) => o.label).join("-")}
      enableFullWidth={false}
      label={"Store"}
      options={storeOptions}
      defaultVal={selectedStore}
      selectionCheck={(option, value) => option.label === value.label}
      onChangeCall={handleOnChange}
    />
  ) : null;
};

export default CampaignStoreSingleSelectFilter;
